import React, { useContext, useEffect, useState, useMemo } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { usePusherContext } from "app/context/pusherContext";
import { actions } from "app/modules/Auth/_redux/authRedux";
import baseApiHelper from "app/redux/baseApiHelper";
import io from 'socket.io-client';
import { cloneDeep, findIndex, find } from "lodash";
import { toast } from "react-toastify";
import store from "redux/store";

const domain = 'sapi.ethizo.com';//'sapi-preprod.ethizo.com';
const port = '';//':8080';
const connectionOptions =  {
  "autoConnect": false,
  "force new connection" : true,
  "reconnectionAttempts": "Infinity", //avoid having user reconnect manually in order to prevent dead clients after a server restart
  "timeout" : 10000 //before connect_error and connect_timeout are emitted.
};
const encounterID = window.location.href.split('/provider-note/')[1] || window.location.search.split('&id=')[1];
const socket = io('https://' + domain + port + '/', connectionOptions);
const SocketContext = React.createContext();

const useSocketContext = () => {
  return useContext(SocketContext);
};

const SocketWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const { selectedNote } = useSelector(
    ({ providerNote }) => ({
      selectedNote: providerNote.selectedNote
    }),
    shallowEqual
  );
  const { setPusherData } = usePusherContext();
  const { user, authToken } = useSelector(
    ({ auth }) => ({
      user: auth.user,
      authToken: auth.authToken
    }),
    shallowEqual
  );
  const authSocketObj = {
    "token": authToken,
    "id": user?.id,
    "name": {
      title: user?.title,
      fname: user?.fname,
      lname: user?.lname
    }
  };
  const unlockRequestTimOutMins = 1; // 1 minutes
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [noteState, setNoteState] = useState([]);
  const [socketNoteSections, setSocketNoteSections] = useState([]);
  const [socketNoteSectionsUpdated, setSocketNoteSectionsUpdated] = useState(false);
  const [unlockReceivedRequests, setUnlockReceivedRequests] = useState([]);
  const [unlockRequestInterval, setUnlockRequestInterval] = useState(null);
  const [unlockSentRequestTimer, setUnlockSentRequestTimer] = useState(null);
  useEffect(() => {
    if (selectedNote?.notes && selectedNote?.notes?.length > 0 && !isConnected) {
      socket.connect();
      socket.on('connect', (res) => {
        setIsConnected(true);
        OpenNote(encounterID);
        dispatch(actions.setSocket(socket));
      });
      socket.on('broadcast_note', (response) => {
        switch (response.key) {
          case 'e10x_typing':
            let storeState = store.getState();
            setSocketNoteSections(cloneDeep((storeState?.providerNote?.selectedNote?.notes)).map(tt => {
              let indexToUpdate = findIndex(tt.sections, { slug: response.data.section });
              if (indexToUpdate > -1) {
                tt.sections[indexToUpdate].socket_update = new Date().getTime();
                tt.sections[indexToUpdate].note_text = response.data.text;
              }
              return tt;
            }));
            break;
            case 'e10x_save_text':
              setPusherData(response.data);
              if (response?.data?.tabs_data && response?.data?.tabs_data.length > 0) {
                setSocketNoteSections(response?.data?.tabs_data);
              }
              if (response.data?.clinical_data) {
                baseApiHelper().updateNoteSliceField("clinical_data", response.data?.clinical_data);
              }
          break;
          case 'e10x_auto_accept':
              if (response?.data?.response) {
                baseApiHelper().processAutoAcceptNote(response?.data?.response, response?.data?.add_to_favorite, response?.data?.is_accept_json);
                let receivedComponent = response?.data?.response?.meta?.auto_accept_response?.component;
                baseApiHelper().invalidateThroughSocket(receivedComponent);
            }
          break;
          case 'e10x_invalidate':
              if (response?.data?.component == 'e10x_sections_list_updated') {
                toast.warn((response?.data?.extra_data?.title ? (response?.data?.extra_data?.title + ' ') : '') + response?.data?.extra_data?.fname + ' ' + response?.data?.extra_data?.lname + ' has updated the Note Sections!');
                setSocketNoteSectionsUpdated(true);
                if (response?.data?.component) {
                  baseApiHelper().invalidateTags(["ProviderNoteSectionsFullList"]);
                  baseApiHelper().invalidateThroughSocket('update_provider_note');
                }

              } else {
                if (response?.data?.component) {
                  baseApiHelper().invalidateThroughSocket(response?.data?.component, response?.data?.extra_data);
                }
                if (response?.data?.component && response?.data?.component == 'family_planning') {
                  let extraData = response?.data?.extra_data;
                  if (extraData && extraData?.response?.data?.notes) {
                    setSocketNoteSections(extraData?.response?.data?.notes);
                    baseApiHelper().updateNoteSliceField('notes', extraData?.response?.data?.notes);
                  }
                }
              }
          break;
          case 'e10x_toggle_family_planning':
              console.warn(":::::: e10x_toggle_family_planning ::::::", response);
          break;
        }
      });
      socket.on('note_state', (data) => {
        //console.warn(":::::: NOTE_STATE ::::::", data);
        setNoteState(data);
      });
  
      socket.on('signed_note', (data) => {
        window.location.reload();
      });
  
      socket.on('deleted_note', (data) => {
        window.location.reload();
      });
  
      socket.on('unlock_section_request', (data) => {
        let tempUnlockReceivedRequests = cloneDeep(unlockReceivedRequests);
        let tempData = cloneDeep(data);
            tempData.timeout = (new Date( Date.now() + ((1000 * unlockRequestTimOutMins) * 60) ));
            tempUnlockReceivedRequests.push(tempData);
        setUnlockReceivedRequests(tempUnlockReceivedRequests);
      });
  
      socket.on('unlock_section_request_response', (data) => {
        if (data.auth?.name && data.accept==0) {
          toast.warn((data.auth?.name?.title ? (data.auth?.name?.title + ' ') : '') + data.auth?.name?.fname + ' ' + data.auth?.name?.lname + ' rejected the Unlock Request!');
        } else if (data.accept==1) {
          broadcast_forceUnlockSection({slug: data?.section});
        }
      });
  
      socket.on('unlock_section_force', (data) => {
        //console.warn(":::::: unlock_section_force :::::::", data);
      });
  
      socket.on('unlock_section_request_cancel', (data) => {
        //console.warn(":::::: UNLOCK_SECTION_FORCE :::::::", data);
      });
  
      socket.on('processed_note', () => {
      });
  
      socket.on('clinical_section_note', () => {
      });
  
      socket.on('disconnect', () => {
        setIsConnected(false);
      });
      return () => {
        // socket.off('connect');
        // socket.off('disconnect');
      };
    }
  }, [selectedNote?.notes]);

  const disconnectSocket = (encounterID) => {
    console.warn(":::::: SOCKET_DISCONNECT ::::::", encounterID);
    socket.emit('close_note', {
      "encounter_id": encounterID,
    });
    socket.disconnect();
  }

  const OpenNote = (encounterID) => {
    console.warn(":::::: OPEN_NOTE ::::::", encounterID);
    socket.emit('open_note', {
      "encounter_id": encounterID,
      "auth": authSocketObj
    });
  }

  const typeInNoteSection = (passedSlug, passedText, passedTab) => {
    broadcastNoteData('e10x_typing', {
      "section": passedSlug,
      "tab": passedTab,
      "text": passedText
    });
  };

  //Lock method
  const lockSection = (passedSlug) => {
    if (passedSlug == 'documate' || passedSlug == 'esync' || passedSlug == 'esync_documate') {
      return;
    }
    let emitData = {
      "encounter_id": encounterID,
      "auth": authSocketObj,
      "section": passedSlug,
      "text": ''
    };
    socket.emit('lock_section', emitData);
  };

  //UnLock method and call cb on the success of unlock
  const unLocksSection = (passedSlug, passedText,cb) => {
    let emitData = {
      "encounter_id": encounterID,
      "auth": authSocketObj,
      "section": passedSlug,
      "text": ''
    };
    socket.emit('unlock_section', emitData,()=>{
      cb();
    });
  };

  //Get the name of user who applied lock

  const getLockedBy = (slug) => {
    if (noteState && noteState?.sections) {
         const section = noteState.sections.find(section=> section.id === slug);
         return (section && (section.locked_by?.token !== authToken) && section?.locked) ? section : null;
        //  if(section) {
        //    if( (section.locked_by?.token !== authToken) && section?.locked) {
        //       return section?.locked_by;
        //    } else {
        //      return null;
        //    }
        //  } else {
        //    return null;
        //  }
    } else return null;
  };

  //Get the lock on the basis of slug and user mine check for the user by which lock applied

  const getLock = (slug,checkMine) => {
    if (noteState && noteState?.sections) {
         const section = noteState.sections.find(section=> section.id === slug);
         if(section) {
           if((checkMine && section.locked_by?.token === authToken) || (!checkMine &&  section.locked_by?.token !== authToken) && section?.locked) {
              return true;
           } else {
             return false;
           }
         } else {
           return false;
         }
    } else return false;
  };

  const getUnlockRequests = (slug, checkMine) => {
    if (noteState && noteState?.sections) {
         const section = noteState.sections.find(section=> section.id === slug);
         if(section && section.requestor) {
           if((checkMine && section.requestor?.token === authToken) || (!checkMine &&  section.requestor?.token !== authToken) && section?.locked) {
              return {...section.requestor, timeout: (new Date( Date.now() + ((1000 * unlockRequestTimOutMins) * 60) ))};
           } else {
             return false;
           }
         } else {
           return false;
         }
    } else return false;
  };

  const broadcastNoteData = (passedKey, passedData) => {
      let emitData = {
        "encounter_id": encounterID,
        "key": passedKey,
        "data": passedData
      };
      socket.emit('broadcast_note', emitData);
  };
  
    const broadcast_invalidate = (passedData) => {
      let emitData = {
        "encounter_id": encounterID,
        "key": 'e10x_invalidate',
        "data": {
          "component": passedData?.component,
          "extra_data": passedData?.extra_data
        }
      };
      socket.emit('broadcast_note', emitData);
    };
  
  const broadcast_signNote = () => {
    let emitData = {
      "encounter_id": encounterID,
    };
    socket.emit('signed_note', emitData);
    disconnectSocket(encounterID);
  };
  
  const broadcast_deleteNote = () => {
    let emitData = {
      "encounter_id": encounterID,
    };
    socket.emit('deleted_note', emitData);
    disconnectSocket(encounterID);
  };
  
  const broadcast_unlockSectionRequest = (passedSection) => {
    let emitData = {
        "encounter_id": encounterID,
        "auth": authSocketObj,
        "section": passedSection?.slug
    };
    socket.emit('unlock_section_request', emitData);
  };
  
  const broadcast_respondUnlockSectionRequest = (passedSection, unlockRequester, accept) => {
    let emitData = {
      "encounter_id": encounterID,
      "auth": authSocketObj,
      "section": passedSection?.slug,
      "accept": accept,
      "requestor_sid": unlockRequester?.requestor_sid,
    };
    socket.emit('unlock_section_request_response', emitData);
  };
  
  const broadcast_forceUnlockSection = (passedSection) => {
    let emitData = {
        "encounter_id": encounterID,
        "auth": authSocketObj,
        "section": passedSection?.slug
    };
    socket.emit('unlock_section_force', emitData);
  };
  
  const broadcast_cancelUnlockSectionRequest = (passedSection) => {
    let emitData = {
        "encounter_id": encounterID,
        "auth": authSocketObj,
        "section": passedSection?.slug
      };
      socket.emit('unlock_section_request_cancel', emitData);
    };
    
    const broadcast_unlockSectionAdmin = (section_slug) => {
      let emitData = {
        "auth": authSocketObj,
        "encounter_id": encounterID,
        "section": section_slug,
        "orders_from_above": true
    };
    socket.emit('unlock_section_admin', emitData);
  };

  const defaultValues = {
    unlockRequestTimOutMins,
    isConnected,
    socketNoteSections,
    setSocketNoteSections,
    socketNoteSectionsUpdated,
    setSocketNoteSectionsUpdated,
    OpenNote,
    noteState,
    typeInNoteSection,
    lockSection,
    unLocksSection,
    getLock,
    getLockedBy,
    getUnlockRequests,
    unlockReceivedRequests,
    setUnlockReceivedRequests,
    unlockRequestInterval,
    setUnlockRequestInterval,
    unlockSentRequestTimer,
    setUnlockSentRequestTimer,
    broadcastNoteData,
    broadcast_invalidate,
    broadcast_signNote,
    disconnectSocket,
    broadcast_deleteNote,
    broadcast_unlockSectionRequest,
    broadcast_respondUnlockSectionRequest,
    broadcast_forceUnlockSection,
    broadcast_cancelUnlockSectionRequest,
    broadcast_unlockSectionAdmin
  };

  return (
    <SocketContext.Provider value={defaultValues}>
      {children}
    </SocketContext.Provider>
  );
};
export { SocketWrapper as default, useSocketContext, SocketContext};